import { Injectable } from '@angular/core';
import { EntityType } from 'breeze-client';
import { first } from 'rxjs/operators';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { AsideModalSize, HelperService } from '../../../core/services/helper.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { PurchaseOrder, PurchaseOrderInvoice } from '../../../data/models';
import { JobsInvoiceSettingsService } from '../../../data/repositories/jobs/jobs-invoice-settings.service';
import { RepoCommonService } from '../../../data/repositories/repo-common.service';
import { RepoJobsService } from '../../../data/repositories/repo-jobs.service';
import { BreezeService } from '../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { PurchaseOrderInvoiceComponent } from './purchase-order-invoice.component';
import { PurchaseOrdersServiceBase } from './purchase-orders.base.service';

@Injectable({ providedIn: 'root' })
export class PurchaseOrdersInvoiceService extends PurchaseOrdersServiceBase {
    constructor(
        lds: LaunchDarklyService,
        breezeService: BreezeService,
        private helperService: HelperService,
        sharedData: SharedDataService,
        repoJobs: RepoJobsService,
        repoCommon: RepoCommonService,
        jobsSettingsService: JobsInvoiceSettingsService,
        logger: BxLogger
    ) {
        super(breezeService, lds, sharedData, repoJobs, repoCommon, jobsSettingsService, logger);
    }

    async addOrderInvoice(order: PurchaseOrder) {
        const newInvoice = this.repoJobs.CreatePurchaseOrderInvoice(order);
        await this.viewOrderInvoice(newInvoice, false);
    }

    async viewOrderInvoice(invoice: PurchaseOrderInvoice, isBillBeingImportedIntoPurchaseOrder: boolean) {
        // debatable whether this is really necessary, but just calling it to ensure order invoices are loaded
        await this.repoJobs.GetPurchaseOrderInvoices(invoice.ID_PurchaseOrder);
        const initialState = {
            invoice,
            isBillBeingImportedIntoPurchaseOrder,
        };
        const modalRef = this.helperService.showModalAside(PurchaseOrderInvoiceComponent, { initialState }, AsideModalSize.medium);
        // @ts-ignore TS2532
        const result = await modalRef.content.action.pipe(first()).toPromise();
        if (result.cancelled) {
            if (!isBillBeingImportedIntoPurchaseOrder) {
                this._cancelOrderInvoice(invoice);
                throw result;
            }
        } else {
            await this._saveOrderInvoice(invoice);
            return invoice;
        }
    }

    async _saveOrderInvoice(invoice: PurchaseOrderInvoice) {
        const order = invoice.PurchaseOrder;
        order.calculateInvoiceTotal();
        await this.setOrderNumber(order);
        await this.saveOrder(order);
        await this.syncOrder(order);
        return invoice;
    }

    _cancelOrderInvoice(invoice: PurchaseOrderInvoice) {
        const order = invoice.PurchaseOrder;
        const orderInvoiceItemType = this.breezeService.manager.metadataStore.getEntityType('PurchaseOrderInvoiceItem') as EntityType;
        const changes = this.breezeService.manager.getChanges([orderInvoiceItemType]);
        changes.forEach((item) => {
            item.entityAspect.rejectChanges();
        });
        invoice.entityAspect.rejectChanges();
        if (
            order.OrderStatus === 'PartReceived' &&
            !order.PurchaseOrderInvoices.some((i) => {
                return !i.IsDeleted;
            })
        )
            order.OrderStatus = 'Sent';
        order.calculateInvoiceTotal();
        order.calculateTotal();
        order.setSupplierInvoiceRef();
    }
}
